/*
 * Copyright (c) 2026 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import { useState, useEffect, useContext } from 'react';
import { Form, Button, Stack } from 'react-bootstrap';
import { getSetting, setSetting } from '../../data';
import { SettingsContext } from '../SettingsContext';
import { useTheme } from 'styled-components';

const JOBS_SETTING_KEY = '/taskManager/jobsConcurrency';
const TASKS_SETTING_KEY = '/taskManager/tasksConcurrency';

function sanitizeConcurrency(value: string, fallback: string) {
    const parsed = parseInt(String(value || '').trim(), 10);
    if (!Number.isFinite(parsed) || parsed < 1) {
        return fallback;
    }
    return String(parsed);
}

function TaskManagerSettings() {
    const theme = useTheme() as any;
    const sectionBorder = `1px solid ${theme?.colors?.ctext || '#ccc'}`;
    const settingsContext = useContext(SettingsContext);
    const [jobsConcurrency, setJobsConcurrency] = useState<string>('1');
    const [originalJobsConcurrency, setOriginalJobsConcurrency] = useState<string>('1');
    const [tasksConcurrency, setTasksConcurrency] = useState<string>('20');
    const [originalTasksConcurrency, setOriginalTasksConcurrency] = useState<string>('20');
    const [refresh, setRefresh] = useState<number>(0);

    const dirty = jobsConcurrency !== originalJobsConcurrency || tasksConcurrency !== originalTasksConcurrency;

    useEffect(() => {
        settingsContext.setIsDirty(dirty);
    }, [dirty, settingsContext]);

    useEffect(() => {
        async function load() {
            try {
                const [jobsRaw, tasksRaw] = await Promise.all([
                    getSetting(JOBS_SETTING_KEY).catch(() => '1'),
                    getSetting(TASKS_SETTING_KEY).catch(() => '20'),
                ]);
                const nextJobs = sanitizeConcurrency(jobsRaw || '1', '1');
                const nextTasks = sanitizeConcurrency(tasksRaw || '20', '20');
                setJobsConcurrency(nextJobs);
                setOriginalJobsConcurrency(nextJobs);
                setTasksConcurrency(nextTasks);
                setOriginalTasksConcurrency(nextTasks);
            } catch (err: any) {
                console.error('Error loading task manager settings:', err);
            }
        }
        load();
    }, [refresh]);

    async function save() {
        const nextJobs = sanitizeConcurrency(jobsConcurrency, '1');
        const nextTasks = sanitizeConcurrency(tasksConcurrency, '20');
        await setSetting(JOBS_SETTING_KEY, nextJobs, 'Maximum number of concurrently running jobs queue probes');
        await setSetting(TASKS_SETTING_KEY, nextTasks, 'Maximum number of concurrently running tasks queue probes');
        setJobsConcurrency(nextJobs);
        setTasksConcurrency(nextTasks);
        setRefresh((value) => value + 1);
    }

    const changedStyle = (changed: boolean): React.CSSProperties => changed ? {
        borderLeft: `3px solid ${theme?.colors?.info || '#0dcaf0'}`,
        paddingLeft: '0.75rem'
    } : {};

    return (
        <div>
            <div className="d-flex justify-content-between align-items-start mb-4" style={{ borderBottom: sectionBorder, paddingBottom: '0.75rem' }}>
                <div>
                    <h3 className="mb-1">Task Manager</h3>
                    <div className="text-muted">Concurrency limits for the jobs and tasks probe queues.</div>
                </div>
                {dirty && (
                    <Stack direction="horizontal" gap={2}>
                        <Button
                            variant="outline-secondary"
                            onClick={() => {
                                setJobsConcurrency(originalJobsConcurrency);
                                setTasksConcurrency(originalTasksConcurrency);
                            }}
                        >
                            Clear
                        </Button>
                        <Button variant="primary" onClick={save}>Save</Button>
                    </Stack>
                )}
            </div>

            <div className="mb-4" style={changedStyle(jobsConcurrency !== originalJobsConcurrency)}>
                <Form.Label htmlFor="jobsConcurrency" className="fw-semibold">Jobs Queue Concurrency</Form.Label>
                <div className="text-muted small mb-2">Maximum number of priority jobs that may execute at the same time.</div>
                <Form.Control
                    id="jobsConcurrency"
                    name="jobsConcurrency"
                    type="number"
                    min={1}
                    step={1}
                    value={jobsConcurrency}
                    onChange={(e) => setJobsConcurrency(e.target.value)}
                    style={{ maxWidth: '18rem' }}
                    placeholder="1"
                />
            </div>

            <div className="mb-4" style={changedStyle(tasksConcurrency !== originalTasksConcurrency)}>
                <Form.Label htmlFor="tasksConcurrency" className="fw-semibold">Tasks Queue Concurrency</Form.Label>
                <div className="text-muted small mb-2">Maximum number of normal queued tasks that may execute at the same time.</div>
                <Form.Control
                    id="tasksConcurrency"
                    name="tasksConcurrency"
                    type="number"
                    min={1}
                    step={1}
                    value={tasksConcurrency}
                    onChange={(e) => setTasksConcurrency(e.target.value)}
                    style={{ maxWidth: '18rem' }}
                    placeholder="20"
                />
            </div>
        </div>
    );
}

export default TaskManagerSettings;